import AppStore from 'AppStore'
import { pages } from 'types'

interface ListViewState<T = any> {
  filters?: T
  scrollY?: number
}

const HISTORY_STATE_KEY = '__specialQuizListViews'
const SESSION_STORAGE_PREFIX = '@SpecialQuiz:listView:'

const getScopedKey = (page: pages) =>
  `${AppStore.application}:${AppStore.environment}:${AppStore.user?._id ?? 'anonymous'}:${page}`

const clone = <T,>(value: T): T => {
  if (value === undefined || value === null) return value
  return JSON.parse(JSON.stringify(value))
}

const getHistoryState = <T,>(page: pages): ListViewState<T> | undefined => {
  if (typeof window === 'undefined') return undefined
  const views = window.history.state?.[HISTORY_STATE_KEY]
  return views?.[getScopedKey(page)]
}

const getSessionState = <T,>(page: pages): ListViewState<T> | undefined => {
  if (typeof window === 'undefined') return undefined

  try {
    const value = window.sessionStorage.getItem(SESSION_STORAGE_PREFIX + getScopedKey(page))
    return value ? JSON.parse(value) : undefined
  } catch (error) {
    return undefined
  }
}

const getListViewState = <T,>(page: pages): ListViewState<T> | undefined =>
  getHistoryState<T>(page) ?? getSessionState<T>(page)

const persistListViewState = <T,>(page: pages, update: Partial<ListViewState<T>>) => {
  if (typeof window === 'undefined') return

  const scopedKey = getScopedKey(page)
  const previousState = getListViewState<T>(page) ?? {}
  const nextState = { ...previousState, ...clone(update) }

  try {
    const historyState = window.history.state && typeof window.history.state === 'object'
      ? window.history.state
      : {}
    const views = historyState[HISTORY_STATE_KEY] ?? {}
    window.history.replaceState(
      {
        ...historyState,
        [HISTORY_STATE_KEY]: { ...views, [scopedKey]: nextState },
      },
      document.title
    )
  } catch (error) {}

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_PREFIX + scopedKey, JSON.stringify(nextState))
  } catch (error) {}
}

export const getListFilters = <T extends Record<string, any>>(page: pages, defaults: T): T => {
  const storedFilters = getListViewState<T>(page)?.filters
  return {
    ...clone(defaults),
    ...(storedFilters ? clone(storedFilters) : {}),
  }
}

export const persistListFilters = <T extends Record<string, any>>(page: pages, filters: T) => {
  persistListViewState(page, { filters })
}

export const getListScrollPosition = (page: pages): number | undefined =>
  getListViewState(page)?.scrollY

export const persistListScrollPosition = (page: pages, scrollY: number) => {
  persistListViewState(page, { scrollY: Math.max(0, Math.round(scrollY)) })
}

export const isListPage = (page?: pages) => Boolean(page && [
  pages.USER_LIST,
  pages.QUESTION_LIST,
  pages.QUESTION_STORED_LIST,
  pages.QUESTION_REVISION,
  pages.PACK_LIST,
  pages.GET_ARTICLES,
  pages.TOPIC_LIST,
  pages.SUBTOPIC_LIST,
  pages.REFERRAL_CAMPAIGNS_PAGE,
].includes(page))
